import React from 'react';
import { useTheme } from '../../context/ThemeContext';

interface Payment {
  id: string;
  amount: number;
  status: string;
  dueDate: string;
  invoiceNumber?: string;
  description?: string;
  payabliToken?: string;
  payabliEntryId?: string;
  currency?: string;
  paymentType?: string;
  createdAt?: string;
  updatedAt?: string;
  statementDate?: string;
  paymentDate?: string;
  paymentIdentity?: {
    id: string;
    email: string;
    phone: string | null;
    firstName: string;
    lastName: string;
    uniqueId: string;
    guarantorLastName: string;
  };
  totalDueAmount?: number;
}

interface UserInfo {
  name: string;
  gn: string;
}

interface PaymentFormProps {
  payment: Payment;
  userInfo: UserInfo;
  loadPayabliScript: (payment: Payment) => void;
  isPayButtonVisible?: boolean;
}

/**
 * Per-bill detail view rendered inside PaymentAccordion. Scheduling is owned entirely by the
 * top-level UserInfoHeader box (a single guarantor-level installment plan) — this form no longer
 * carries a schedule config, which removes the old Equal/Manual split scaffolding + the shared-state
 * divergence. A one-time "Pay this bill" button shows only when the caller opts in (isPayButtonVisible).
 */
const PaymentForm: React.FC<PaymentFormProps> = ({
  payment,
  userInfo,
  loadPayabliScript,
  isPayButtonVisible = true
}) => {
  const { theme } = useTheme();
  const isPaymentAlreadyDone = payment.status?.toLowerCase() == "success";

  return (
    <div className="space-y-6">
      {/* Bill details */}
      <div>
        <label className="block text-gray-800 text-sm font-bold mb-3">Bill Details</label>
        <div className="rounded-lg pb-2">
          {[
            ["Total Due", `$${payment.amount || 0}`],
            ["Paid", `$${payment.amount - (payment.totalDueAmount || 0)}`],
            ["Pending", `$${payment.totalDueAmount || 0}`],
            ["Invoice Number", payment.invoiceNumber || payment.id],
            ["Status", payment.status?.toUpperCase() || "N/A"],
            ["Due Date", new Date(payment.dueDate).toLocaleDateString('en-US')],
            ["Patient Name", `${payment.paymentIdentity?.firstName || ''} ${payment.paymentIdentity?.lastName || ''}`.trim() || userInfo?.name || "N/A"],
            ["Patient Email", payment.paymentIdentity?.email || "N/A"],
            ["Patient Phone", payment.paymentIdentity?.phone || "N/A"],
          ].map(([label, value]) => (
            <div key={label} className="flex text-sm font-semibold mb-2">
              <span className="w-40 text-gray-800">{label}:</span>
              <span className="text-gray-500 flex-1">{value || "N/A"}</span>
            </div>
          ))}
        </div>
      </div>

      {/* One-time "pay this bill" button — scheduling lives in the top box */}
      {isPayButtonVisible && (
        <button
          onClick={() => loadPayabliScript(payment)}
          disabled={isPaymentAlreadyDone}
          className={`w-full ${theme["button-bg"]} text-white text-lg font-semibold rounded-lg px-6 py-3 ${theme["home-button-text"]} button-hover ${
            isPaymentAlreadyDone ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          Pay ${payment.amount}
        </button>
      )}
    </div>
  );
};

export default PaymentForm;
